import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import {
  ATTRIBUTION_WINDOWS,
  type AttributionWindow,
  type BdmFilters,
  CADENCES,
  COMPARISONS,
  COVERAGE_SEGMENTS,
  type Cadence,
  type ChartData,
  type Comparison,
  type Kpi,
  SPLITS,
  type Split,
  periodLabel,
} from './model';

const VISIBLE_PERIODS = 12;

export type PageChart = { title: string; data: ChartData | null; truncated: boolean };

export function agentHubBdmPageView(input: {
  ready: boolean;
  filters: BdmFilters;
  bdmOptions: Array<string>;
  period: string;
  comparisonPeriod: string;
  kpis: Array<Kpi> | null;
  charts: Array<PageChart>;
}): PageView {
  const { filters } = input;
  const filterSchema: Record<string, PageField> = {
    from: { type: 'date' },
    to: { type: 'date' },
    comparison: { type: 'enum', values: COMPARISONS },
    cadence: { type: 'enum', values: CADENCES },
    attributionWindow: { type: 'enum', values: ATTRIBUTION_WINDOWS.map(String) },
    split: { type: 'enum', values: SPLITS },
    bdm_name: { type: 'enum', values: [null, ...input.bdmOptions] },
    coverage_segment: { type: 'enum', values: [null, ...COVERAGE_SEGMENTS] },
  };
  return {
    title: 'Agent Hub BDM Performance',
    ready: input.ready,
    dataMode: 'live analytics.agent_hub_interactions_bookings and analytics.agent_hub_agency_coverage',
    filters: {
      from: filters.from,
      to: filters.to,
      comparison: filters.comparison,
      cadence: filters.cadence,
      attributionWindow: String(filters.attributionWindow),
      split: filters.split,
      bdm_name: filters.bdm.length === 1 ? filters.bdm[0] : null,
      coverage_segment: filters.segments.length === 1 ? filters.segments[0] : null,
    },
    filterSchema,
    activeSelections: { bdm_name: filters.bdm, coverage_segment: filters.segments },
    period: input.period,
    comparisonPeriod: input.comparisonPeriod,
    kpis: input.kpis?.map(({ label, current, previous, change }) => ({ label, current, previous, change })) ?? null,
    visibleCharts: input.charts.map(({ title, data, truncated }) => ({
      title,
      truncated,
      periods: (data?.categories ?? []).slice(-VISIBLE_PERIODS).map((period) => {
        const index = data.categories.indexOf(period);
        return {
          period: periodLabel(period, filters.cadence),
          ...Object.fromEntries(
            data.panes.flatMap((pane) =>
              pane.series.map((series) => [`${pane.title} · ${series.name}`, series.values[index]]),
            ),
          ),
        };
      }),
    })),
    definitions: {
      source:
        'lux-analytics-prod.analytics.agent_hub_interactions_bookings (BDM interactions and Agent Hub bookings) and analytics.agent_hub_agency_coverage (one row per agency per day), queried live; rebuilt daily.',
      kpis: 'KPIs cover from–to and compare with the comparison window. Charts show every period from 1 Sep 2025 (coverage from 1 Jul 2025) by cadence.',
      measures:
        'Bookings per 100 interactions divides period totals. % agencies converted: interacted agencies whose first booking on or after their first-ever interaction came within the attribution window, over interacted agencies. % new agent bookings: bookings by agencies not yet past their first booking date in that cadence period.',
      filters:
        'bdm_name and coverage_segment accept one value or null for all. activeSelections lists every selected value.',
    },
  };
}

/** Applies a validated askRichard edit; list filters take one value, or null for every value. */
export function editFromBridge(changes: Record<string, unknown>, view: PageView): Partial<BdmFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<BdmFilters> = {};
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  if ('comparison' in changes) {
    next.comparison = changes.comparison as Comparison;
  }
  if ('cadence' in changes) {
    next.cadence = changes.cadence as Cadence;
  }
  if ('attributionWindow' in changes) {
    next.attributionWindow = Number(changes.attributionWindow) as AttributionWindow;
  }
  if ('split' in changes) {
    next.split = changes.split as Split;
  }
  if ('bdm_name' in changes) {
    next.bdm = changes.bdm_name === null ? [] : [changes.bdm_name as string];
  }
  if ('coverage_segment' in changes) {
    next.segments = changes.coverage_segment === null ? [] : [changes.coverage_segment as string];
  }
  return next;
}
